import Joi from "joi-browser";

// Password policy — mirrors the backend policy exactly so the client
// accepts/rejects the same passwords the API does (i.e. the user never submits
// a password the server rejects with HTTP 400). Requires 8-40 characters and at
// least one lowercase letter, one uppercase letter, one number, and one special
// character, and must not contain whitespace. "Special" = any non-whitespace,
// non-alphanumeric character; keep this in sync with getPasswordPolicyError in
// the backend (src/constants/utils.ts).
const MIN_LENGTH = 8;
const MAX_LENGTH = 40;

// The individual requirements, each independently testable so the UI can show
// which ones a password currently satisfies. `meetsPolicy` is derived from this
// list, so the checklist a user sees can never drift from what actually
// validates.
export const PASSWORD_RULES = [
  {
    id: "length",
    label: `${MIN_LENGTH}-${MAX_LENGTH} characters long`,
    test: (value) => value.length >= MIN_LENGTH && value.length <= MAX_LENGTH,
  },
  {
    id: "uppercase",
    label: "At least one uppercase letter",
    test: (value) => /[A-Z]/.test(value),
  },
  {
    id: "lowercase",
    label: "At least one lowercase letter",
    test: (value) => /[a-z]/.test(value),
  },
  {
    id: "number",
    label: "At least one number",
    test: (value) => /[0-9]/.test(value),
  },
  {
    id: "special",
    label: "At least one special character",
    test: (value) => /[^A-Za-z0-9]/.test(value),
  },
  {
    id: "no-spaces",
    label: "No spaces",
    test: (value) => !/\s/.test(value),
  },
];

export const checkPasswordRules = (value) => {
  const password = typeof value === "string" ? value : "";
  return PASSWORD_RULES.map((rule) => ({
    ...rule,
    satisfied: rule.test(password),
  }));
};

const meetsPolicy = (value) =>
  typeof value === "string" && PASSWORD_RULES.every((rule) => rule.test(value));

const password = Joi.extend((joi) => {
  return {
    name: "string",
    base: joi.string(),
    language: {
      policy:
        "Password must be 8-40 characters, contain no spaces, and include an uppercase letter, a lowercase letter, a number, and a special character",
    },
    rules: [
      {
        name: "policy",
        validate(params, value, state, options) {
          if (!meetsPolicy(value)) {
            return joi.createError(
              "string.policy",
              { v: value },
              state,
              options,
            );
          }

          return value;
        },
      },
    ],
  };
});

export default password;
